import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { Bg, Chip, CYAN, DISPLAY, INK, LIME, popIn, PURPLE, slideUp, UI, WHITE, YELLOW } from "./theme";

/* ════════════════════════════════════════════════════════════════════════
   SCENE 2 — THE SOLUTION (frame 150-270 / 5-9s, 120 frame)
   Latar cyan. Dashboard tablet bersih meluncur masuk dari bawah.
   ════════════════════════════════════════════════════════════════════════ */

const STATS = [
  { label: "OMSET HARI INI", value: "Rp 1.240.000", bg: LIME, delay: 30 },
  { label: "TRANSAKSI", value: "32", bg: YELLOW, delay: 36 },
  { label: "KAPSTER AKTIF", value: "8", bg: WHITE, delay: 42 },
  { label: "RATA-RATA / NOTA", value: "Rp 38.750", bg: PURPLE, delay: 48 },
];

const BARS = [42, 68, 55, 88, 61, 96, 74];

export const Scene2Solution: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const tabletIn = spring({ frame: frame - 6, fps, config: { damping: 12, stiffness: 300, mass: 1 } });
  const tabletY = slideUp(frame, fps, 6, 1400, { damping: 12, stiffness: 300, mass: 1 });
  const tabletRot = interpolate(tabletIn, [0, 1], [5, 0]);
  const headline = popIn(frame, fps, 0, { damping: 10, stiffness: 300, mass: 0.9 });

  return (
    <AbsoluteFill style={{ fontFamily: UI, color: INK }}>
      <Bg color={CYAN} pattern="stripes" patternOpacity={0.1}>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-[40px]">
          {/* ── Headline solusi ── */}
          <div
            style={{ transform: `scale(${headline.scale})`, opacity: headline.opacity }}
            className="flex flex-col items-center gap-4"
          >
            <Chip className="bg-black text-[30px] text-white">Solusinya cuma satu</Chip>
            <div
              className="border-4 border-black bg-black px-8 py-2 text-[96px] uppercase leading-[0.95] text-[#22E0FF] shadow-[10px_10px_0px_0px_rgba(255,255,255,1)]"
              style={{ fontFamily: DISPLAY }}
            >
              PANGKASIN!
            </div>
            <div
              className="border-4 border-black bg-white px-6 py-2 text-center text-[44px] uppercase leading-[1.05] text-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
              style={{ fontFamily: DISPLAY }}
            >
              SIKAT HABIS PAKAI PANGKASIN
            </div>
            <div
              className="border-4 border-black bg-[#B8FF2F] px-6 py-2 text-center text-[44px] uppercase leading-[1.05] text-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
              style={{ fontFamily: DISPLAY }}
            >
              SISTEM KASIR ANTI RIBET
            </div>
          </div>

          {/* ── Tablet dashboard ── */}
          <div
            className="w-[980px]"
            style={{ transform: `translateY(${tabletY}px) rotate(${tabletRot}deg)` }}
          >
            <div className="border-[6px] border-black bg-[#111111] p-3 shadow-[14px_14px_0px_0px_rgba(0,0,0,1)]">
              {/* bezel tablet */}
              <div className="border-4 border-black bg-white">
                {/* top bar */}
                <div className="flex items-center justify-between border-b-4 border-black bg-white px-6 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-7 w-7 rounded-full border-4 border-black bg-[#FF3B30]" />
                    <div className="h-7 w-7 rounded-full border-4 border-black bg-[#FFE14D]" />
                    <div className="h-7 w-7 rounded-full border-4 border-black bg-[#B8FF2F]" />
                  </div>
                  <div
                    className="text-[32px] uppercase tracking-[0.14em] text-black"
                    style={{ fontFamily: DISPLAY }}
                  >
                    DASHBOARD PANGKASIN
                  </div>
                  <div
                    className="border-4 border-black bg-[#B8FF2F] px-3 py-1 text-[24px] uppercase"
                    style={{ fontFamily: DISPLAY }}
                  >
                    ONLINE
                  </div>
                </div>

                {/* body */}
                <div className="px-6 py-5">
                  <div className="grid grid-cols-2 gap-4">
                    {STATS.map((s) => {
                      const { scale, opacity } = popIn(frame, fps, s.delay, {
                        damping: 11,
                        stiffness: 300,
                        mass: 0.8,
                      });
                      return (
                        <div
                          key={s.label}
                          className="border-4 border-black px-4 py-3 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
                          style={{ background: s.bg, transform: `scale(${scale})`, opacity }}
                        >
                          <div
                            className="text-[22px] uppercase tracking-wider text-black/70"
                            style={{ fontFamily: DISPLAY }}
                          >
                            {s.label}
                          </div>
                          <div className="mt-1 text-[46px] leading-none text-black" style={{ fontFamily: DISPLAY }}>
                            {s.value}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* grafik batang */}
                  <div className="mt-5 border-4 border-black bg-[#f5f5f5] p-4 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                    <div
                      className="mb-3 text-[24px] uppercase tracking-wider text-black"
                      style={{ fontFamily: DISPLAY }}
                    >
                      Omset 7 Hari Terakhir
                    </div>
                    <div className="flex h-[240px] items-end gap-3 border-b-4 border-black/20">
                      {BARS.map((h, i) => {
                        const grow = spring({
                          frame: frame - (52 + i * 4),
                          fps,
                          config: { damping: 12, stiffness: 220, mass: 0.7 },
                        });
                        return (
                          <div
                            key={i}
                            className="flex-1 border-4 border-black"
                            style={{
                              height: `${Math.min(h * grow, 100)}%`,
                              background: i === BARS.length - 1 ? "#FF2E88" : "#22E0FF",
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>

                  {/* tombol kasir */}
                  {(() => {
                    const { scale, opacity } = popIn(frame, fps, 84, {
                      damping: 10,
                      stiffness: 360,
                      mass: 0.9,
                    });
                    return (
                      <div
                        className="mt-5 flex items-center justify-between border-4 border-black bg-[#FF2E88] px-6 py-4 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
                        style={{ transform: `scale(${scale})`, opacity }}
                      >
                        <div className="text-[38px] uppercase text-white" style={{ fontFamily: DISPLAY }}>
                          BUKA KASIR
                        </div>
                        <div
                          className="border-4 border-black bg-white px-4 py-1 text-[30px] text-black"
                          style={{ fontFamily: DISPLAY }}
                        >
                          1 KLIK
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Bg>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
